import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import React, { lazy, memo, Suspense, useState } from 'react';
import {
	EDIT_KYC_DOCUMENT_FORM_FIELDS,
	useEditKycDocumentDrawerController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycDocumentCard/EditKycDocumentDrawer/useEditKycDocumentDrawerController.ts';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { drawerFooter } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/kycDocumentDrawer.styled';
import { Button, Divider, Drawer, Modal, Text } from '@mantine/core';
import { DocumentTypeBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/DocumentTypeBadge.tsx';
import { TextInput, Select } from 'react-hook-form-mantine';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import {
	CustomDetailCategory,
	DocumentWorkflowTemplateSourceFormat,
} from '@dealstack/shared/lib/generated';
import { categoryOptions } from '@dealstack/frontend/shared/utils/customDetailCategoryOptions.ts';
import type { KycPlacementValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';
import { useKycFormScope } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycFormScopeContext';
import { KycPlacementDraftProvider } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/KycPlacementDraftContext.tsx';

// Lazy: the editor pulls in react-pdf and the whole document surface, and it only ever
// renders inside a modal the admin opens.
const KycFieldBindingEditor = lazy(() =>
	import(
		'@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/FieldBinding/KycFieldBindingEditor.tsx'
	).then((module) => ({ default: module.KycFieldBindingEditor }))
);

interface Props {
	formTemplate: FormTemplate;
	formTemplateFieldKey: string;
	opened: boolean;
	onClose: () => void;
	onPreviewDocument: () => void;
	onUpdateTemplate?: (updates: {
		name?: string;
		instruction?: string;
		category?: CustomDetailCategory | null;
		requiresSignature?: boolean;
		useManualSignaturePlacement?: boolean;
		showStampMetadata?: boolean;
		pendingPlacement?: KycPlacementValue | null;
	}) => Promise<boolean>;
	showCategorySelect?: boolean;
}

const EditKycDocumentDrawer = ({
	formTemplate,
	formTemplateFieldKey,
	opened,
	onClose,
	onPreviewDocument,
	onUpdateTemplate,
	showCategorySelect = false,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const { editKycDocumentForm, onSubmit, onPlacementSaved, pendingPlacement, showStampMetadata } =
		useEditKycDocumentDrawerController(formTemplateFieldKey, onClose, onUpdateTemplate);
	const [editorOpen, setEditorOpen] = useState(false);

	const { organizationId } = useKycFormScope();
	const sourceFormat = formTemplate.sourceFormat ?? DocumentWorkflowTemplateSourceFormat.PDF;
	const isDocx = sourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX;
	// Programme tier only: the data points a field binds to are resolved against a deal's
	// investor, and an organisation default has no deal to resolve against.
	const supportsBindings = !organizationId;

	const requiresSignature = editKycDocumentForm.watch(
		EDIT_KYC_DOCUMENT_FORM_FIELDS.requiresSignature
	);

	// Without bindings the editor is placement alone, which only a signed PDF has; a Word form's
	// editor is its programme-scoped document, so it needs the programme tier.
	const hasEditorJob = supportsBindings || (!isDocx && !!requiresSignature);
	// A Word form's document lives on the server, so it cannot be opened before the wizard
	// saves the form. A PDF can: the box travels with the drawer until there is a template to
	// key it to, which is how placement worked before fields joined it on the same canvas.
	const canOpenEditor = hasEditorJob && !!formTemplate.fileId && !(isDocx && formTemplate.local);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				<Text size="B4Bold">
					{tMipCreate('kyc.documents.edit_drawer.edit_document_details')}
				</Text>
			}
		>
			<div onClick={onPreviewDocument} style={{ cursor: 'pointer' }}>
				<Text size="B5Medium" mb="0.4rem">
					{tMipCreate('kyc.documents.edit_drawer.file_name')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{formTemplate.fileName}
				</Text>
				<DocumentTypeBadge
					text={
						formTemplate.type === CustomDetailEnum.INDIVIDUAL
							? tMipCreate('kyc.documents.edit_drawer.individual_investor')
							: tMipCreate('kyc.documents.edit_drawer.piv_investor')
					}
					iconVariant="folder"
				/>
			</div>

			<Divider mt="1rem" mb="2rem" />

			<form onSubmit={editKycDocumentForm.handleSubmit(onSubmit)}>
				<Text size="B5Medium" mb="0.4rem">
					{tMipCreate('kyc.documents.edit_drawer.name_your_document')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{tMipCreate('kyc.documents.edit_drawer.name_your_document_description')}
				</Text>
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_FORM_NAME_INPUT}>
					<TextInput
						name={EDIT_KYC_DOCUMENT_FORM_FIELDS.name}
						control={editKycDocumentForm.control}
						size="md"
						aria-label={tMipCreate('kyc.documents.edit_drawer.name_your_document')}
						placeholder={tMipCreate('kyc.documents.edit_drawer.enter_file_name')}
						disabled={formTemplate.isPreFillable}
						mb="2rem"
					/>
				</UIElementIdentifier>

				{showCategorySelect && (
					<>
						<Text size="B5Medium" mb="0.4rem">
							{tMipCreate('kyc.documents.edit_drawer.category')}
						</Text>
						<Text size="B6Medium" c="gray.9" mb="0.8rem">
							{tMipCreate('kyc.documents.edit_drawer.category_description')}
						</Text>
						<Select
							name={EDIT_KYC_DOCUMENT_FORM_FIELDS.category}
							control={editKycDocumentForm.control}
							size="md"
							aria-label={tMipCreate('kyc.documents.edit_drawer.category')}
							placeholder="Select category"
							data={categoryOptions}
							clearable
							disabled={formTemplate.isPreFillable}
							mb="2rem"
						/>
					</>
				)}

				<Text size="B5Medium" mb="0.4rem">
					{tMipCreate('kyc.documents.edit_drawer.instructions_to_investors')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{tMipCreate('kyc.documents.edit_drawer.instructions_to_investors_description')}
				</Text>
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_FORM_INSTRUCTION_INPUT}
				>
					<TextInput
						name={EDIT_KYC_DOCUMENT_FORM_FIELDS.instructions}
						control={editKycDocumentForm.control}
						size="md"
						placeholder={tMipCreate(
							'kyc.documents.edit_drawer.enter_your_instructions_here'
						)}
					/>
				</UIElementIdentifier>

				<Text size="B5Medium" mt="2rem" mb="0.4rem">
					{tMipCreate('kyc.documents.edit_drawer.require_electronic_signature')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{tMipCreate('kyc.documents.edit_drawer.require_electronic_signature_desc')}
				</Text>
				<AdvancedSwitch
					selectedValue={requiresSignature}
					onChange={(value) =>
						editKycDocumentForm.setValue(
							EDIT_KYC_DOCUMENT_FORM_FIELDS.requiresSignature,
							!!value
						)
					}
					aria-label={tMipCreate(
						'kyc.documents.edit_drawer.require_electronic_signature'
					)}
				/>

				{/* One entry point for both jobs. The signature box and the fields are placed
				    on the same page and written by the same save, exactly as a master
				    document's are; a Word form has no fixed page, so its editor is the
				    document itself. */}
				{hasEditorJob && (
					<>
						<Text size="B5Medium" mt="2rem" mb="0.4rem">
							{tMipCreate('kyc.documents.edit_drawer.document_setup')}
						</Text>
						<Text size="B6Medium" c="gray.9" mb="0.8rem">
							{tMipCreate(
								isDocx
									? 'kyc.documents.edit_drawer.document_setup_desc_word'
									: 'kyc.documents.edit_drawer.document_setup_desc_pdf'
							)}
						</Text>
						<Button
							variant="default"
							fullWidth
							disabled={!canOpenEditor}
							onClick={() => setEditorOpen(true)}
							data-testid={
								TEST_ELEMENT_IDS.MIP_CREATE_KYC_DRAWER_FIELD_BINDINGS_BUTTON
							}
						>
							{tMipCreate(
								isDocx
									? 'kyc.documents.edit_drawer.edit_document'
									: 'kyc.documents.edit_drawer.set_up_document'
							)}
						</Button>
						{!canOpenEditor && (
							<Text size="B6Medium" c="gray.9" mt="0.4rem">
								{tMipCreate('kyc.documents.edit_drawer.field_bindings_save_first')}
							</Text>
						)}
					</>
				)}
			</form>

			{editorOpen && canOpenEditor && formTemplate.fileId && (
				<Modal
					opened
					fullScreen
					withCloseButton={false}
					padding={0}
					onClose={() => setEditorOpen(false)}
				>
					<Suspense fallback={null}>
						{/* An unsaved form has no template to key a placement row to, so the
						    box it draws comes back here and rides along with the create. */}
						<KycPlacementDraftProvider
							onDraft={onPlacementSaved}
							pending={pendingPlacement}
						>
							<KycFieldBindingEditor
								templateId={formTemplate.local ? null : formTemplate.id}
								fileId={formTemplate.fileId}
								sourceFormat={sourceFormat}
								supportsBindings={supportsBindings}
								requiresSignature={!!requiresSignature}
								initialShowStampMetadata={showStampMetadata}
								onClose={() => setEditorOpen(false)}
							/>
						</KycPlacementDraftProvider>
					</Suspense>
				</Modal>
			)}

			<div style={drawerFooter}>
				<Divider mb="1rem" />

				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_DRAWER_SAVE_BUTTON}
				>
					<Button
						onClick={editKycDocumentForm.handleSubmit(onSubmit)}
						loading={editKycDocumentForm.formState.isSubmitting}
						fullWidth
						mb="1rem"
					>
						{tMipCreate('kyc.documents.edit_drawer.save')}
					</Button>
				</UIElementIdentifier>

				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_DRAWER_CANCEL_BUTTON}
				>
					<Button
						onClick={() => {
							editKycDocumentForm.reset();
							onClose();
						}}
						fullWidth
						variant="default"
					>
						{tMipCreate('kyc.documents.edit_drawer.cancel')}
					</Button>
				</UIElementIdentifier>
			</div>
		</Drawer>
	);
};
const _EditKycDocumentDrawerExport = memo(EditKycDocumentDrawer);

export { _EditKycDocumentDrawerExport as EditKycDocumentDrawer };
